import EventDispatcher from '@/utils/dispatcher'

export type WSEvent = 'open' | 'message' | 'error' | 'close'

export default class WebSocketClient extends EventDispatcher {
  private ws?: WebSocket
  private readonly url: string

  // 是否允许自动重连，由 connect/close/destroy 显式控制
  private readonly reconnectInterval: number
  private readonly maxReconnectAttempts: number
  private shouldReconnect = true
  private reconnectAttempts = 0
  private reconnectTimer?: ReturnType<typeof setTimeout>

  private heartbeatActive = false
  private heartbeatInterval: number
  private heartbeatTimer?: ReturnType<typeof setInterval>

  constructor(
    url: string,
    config: {
      reconnectInterval?: number
      maxReconnectAttempts?: number
      heartbeatInterval?: number
    } = {},
  ) {
    super()
    this.url = url
    this.reconnectInterval = config.reconnectInterval ?? 1000
    this.maxReconnectAttempts = config.maxReconnectAttempts ?? 10
    this.heartbeatInterval = config.heartbeatInterval ?? 30000
  }

  public get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN
  }
  public send(data: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(data)
    } catch {
      /* 吞掉，交由上层重试策略 */
    }
  }

  public connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING)
      return
    this.clearReconnectTimer()
    // 每次新连都恢复允许自动重连（避免上次 close(false) 遗留）
    this.shouldReconnect = true
    this.ws = new WebSocket(this.url)

    this.ws.onopen = (e) => {
      this.reconnectAttempts = 0
      this.dispatchEvent('open', e)
      this.startHeartbeat()
    }

    this.ws.onmessage = (e) => this.dispatchEvent('message', e.data)

    this.ws.onerror = (e) => this.dispatchEvent('error', e)

    this.ws.onclose = (e) => {
      this.dispatchEvent('close', e)
      this.stopHeartbeat()

      const closed = this.ws
      if (closed) closed.onopen = closed.onmessage = closed.onerror = closed.onclose = null
      this.ws = undefined

      if (this.shouldReconnect === false || this.reconnectAttempts >= this.maxReconnectAttempts)
        return
      this.scheduleReconnect()
    }
  }

  public close(code?: number, reason?: string, allowReconnect = false): void {
    this.shutdown('close', { code, reason, allowReconnect })
  }

  public destroy(code = 1000, reason = 'client destroy'): void {
    this.shutdown('destroy', { code, reason })
  }

  private shutdown(
    kind: 'close' | 'destroy',
    opts: { code?: number; reason?: string; allowReconnect?: boolean } = {},
  ): void {
    const { code, reason, allowReconnect } = opts

    this.shouldReconnect = kind === 'close' ? !!allowReconnect : false

    this.clearReconnectTimer()
    this.stopHeartbeat()

    if (this.ws) {
      if (kind === 'destroy') {
        this.ws.onopen = this.ws.onclose = this.ws.onerror = this.ws.onmessage = null
      }
      try {
        code !== undefined ? this.ws.close(code, reason) : this.ws.close()
      } catch {
        console.error(`${kind} error`, code, reason)
      }
      if (kind === 'destroy') this.ws = undefined
    }

    if (kind === 'destroy') this.removeAllListeners()
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++
    const delay = this.calcBackoffDelay()
    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, delay)
  }

  private calcBackoffDelay(): number {
    const base = Math.min(30000, this.reconnectInterval * 2 ** this.reconnectAttempts)
    const jitter = Math.floor(base * 0.2 * Math.random()) // 20% 抖动
    return base + jitter
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatActive || !this.ws) return
    this.heartbeatActive = true
    this.heartbeatTimer = setInterval(() => {
      // 复杂逻辑：仅在 OPEN 状态发送心跳，避免异常
      if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send('ping')
    }, this.heartbeatInterval)
  }

  private stopHeartbeat(): void {
    if (!this.heartbeatActive) return
    this.heartbeatActive = false
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = undefined
    }
  }
}
